/**
 * serviceColors.js
 *
 * Maps known service keys to brand colors.
 * Falls back gracefully to a rotating palette for any unknown/new service
 * that comes back from the dynamic enabledServices API.
 */

const KNOWN = {
  FSM:  { color: '#0F6E56', bg: '#E1F5EE', label: 'FSM',  icon: '🔧' },
  HRMS: { color: '#185FA5', bg: '#E6F1FB', label: 'HRMS', icon: '👥' },
  ESS:  { color: '#854F0B', bg: '#FAEEDA', label: 'ESS',  icon: '👤' },
  CRM:  { color: '#993556', bg: '#FBEAF0', label: 'CRM',  icon: '🏪' },
  ERP:  { color: '#3730A3', bg: '#EEF2FF', label: 'ERP',  icon: '📊' },
  SCM:  { color: '#065F46', bg: '#D1FAE5', label: 'SCM',  icon: '🚚' },
  POS:  { color: '#7C2D12', bg: '#FEF3C7', label: 'POS',  icon: '🏷️' },
  LMS:  { color: '#1E3A5F', bg: '#DBEAFE', label: 'LMS',  icon: '📚' },
};

// Rotating fallback palette for completely new services
const FALLBACK_PALETTE = [
  { color: '#4338CA', bg: '#EEF2FF' },
  { color: '#0891B2', bg: '#E0F7FA' },
  { color: '#7C3AED', bg: '#F5F3FF' },
  { color: '#B45309', bg: '#FFF7ED' },
  { color: '#0F766E', bg: '#F0FDFA' },
  { color: '#BE185D', bg: '#FDF2F8' },
];

const _cache = {};

/**
 * Resolves a raw service name (as it comes back from enabledServices) to one
 * of the KNOWN keys above, tolerating real-world variants like "HRMS TEAM",
 * "ESS Portal", or "FSM_v2" — not just an exact "HRMS"/"ESS"/"FSM" string.
 * Tries an exact match first (fast path), then splits the name into tokens
 * on any non-alphanumeric character and looks for a token equal to a known
 * key. This deliberately does NOT match substrings inside a single word
 * (e.g. "MyHRMSExtra" or "PROCESS" won't match HRMS/ESS) — only a whole,
 * separated token counts. Returns null if nothing known matches, so callers
 * can fall back to their own default.
 */
export function matchKnownServiceKey(name) {
  if (!name) return null;
  if (KNOWN[name]) return name;
  const tokens = String(name).toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  return Object.keys(KNOWN).find((k) => tokens.includes(k)) || null;
}

export function getServiceMeta(key) {
  const knownKey = matchKnownServiceKey(key);
  if (knownKey) return KNOWN[knownKey];
  if (_cache[key]) return _cache[key];

  // Assign a stable fallback based on key hash so it doesn't change on re-render
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) & 0xffff;
  const pal = FALLBACK_PALETTE[hash % FALLBACK_PALETTE.length];

  _cache[key] = { ...pal, label: key, icon: '⚙️' };
  return _cache[key];
}
